import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";

import breakpoints from "assets/theme/base/breakpoints";

// Data
import tutorTableData from "Views/people/tutor/data/tutorTableData";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import { Warning, Close, Delete } from "@mui/icons-material";
import ArgonInput from "components/ArgonInput";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import ArgonButton from "components/ArgonButton";
import NotificationTableData from "Views/Communication/notification/data/notificationTableData";
import { Link, useNavigate } from "react-router-dom";
import api from "utils/api";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const navigate = useNavigate();

  // State management
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle search loading
  useEffect(() => {
    if (searchQuery.trim()) {
      setSearchLoading(true);
      const timer = setTimeout(() => {
        setSearchLoading(false);
      }, 300); // Small delay to show loading state
      return () => clearTimeout(timer);
    } else {
      setSearchLoading(false);
    }
  }, [searchQuery]);

  // Delete button click handler
  const handleDeleteClick = (notification) => {
    setSelectedNotification(notification);
    setDeleteDialog(true);
  };

  // Edit button click handler
  const handleEditClick = (notification) => {
    navigate(`/notification/edit/${notification._id}`, {
      state: { notification },
    });
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setSelectedNotification(null);
  };

  // Delete notification
  const handleDeleteConfirm = async () => {
    if (!selectedNotification) return;

    try {
      setLoading(true);
      const response = await api.delete(`/notification/${selectedNotification._id}`);

      if (response.data.success) {
        showSnackbar("Notification deleted successfully", "success");
        // Refresh the table data
        refetch();
      } else {
        showSnackbar(response.data.message || "Failed to delete notification", "error");
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to delete notification", "error");
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const {
    columns,
    rows,
    loading: tableLoading,
    refetch,
  } = NotificationTableData(handleEditClick, handleDeleteClick, searchQuery);

  const tabsData = [
    { label: "All", count: 12, color: "#2027e1ff", content: "" },
    { label: "Pending", count: 5, color: "#ec3c10ff", content: "" },
    { label: "Approved", count: 8, color: "#ec10c7ff", content: "" },
    { label: "Rejected", count: 2, color: "#10a6ecff", content: "" },
    { label: "Deactivated", count: 4, color: "#1a6808b7", content: "" },
  ];

  // Derive paginated rows on client side
  const totalItems = rows?.length || 0;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pagedRows = rows?.slice(startIndex, startIndex + rowsPerPage) || [];

  const handlePageChange = (event, value) => setCurrentPage(value);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection={{ xs: "column", sm: "row" }}
                mb={2}
              >
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Notifications & Notices
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonInput
                    placeholder="Search notifications..."
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <ArgonButton
                    onClick={() => {
                      console.log("🔄 Manual refresh clicked");
                      setCurrentPage(1);
                      refetch();
                    }}
                    variant="outlined"
                    size="small"
                    color="info"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Refresh
                  </ArgonButton>
                  <ArgonButton
                    component={Link}
                    to="/notification/add"
                    variant="contained"
                    size="small"
                    color="primary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Add Notification
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {tableLoading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Loading notifications...
                  </ArgonTypography>
                </ArgonBox>
              ) : searchLoading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Searching notifications...
                  </ArgonTypography>
                </ArgonBox>
              ) : rows && rows.length > 0 ? (
                <>
                  <Table columns={columns} rows={pagedRows} />

                  {/* Pagination Controls */}
                  <PaginationBar
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalItems}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </>
              ) : (
                <ArgonBox textAlign="center" py={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h4" color="text" fontWeight="medium" mb={1}>
                      📭
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium" mb={2}>
                    {searchQuery.trim()
                      ? "No notifications found matching your search"
                      : "No notifications available"}
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="secondary" mb={3}>
                    {searchQuery.trim()
                      ? "Try adjusting your search terms or clear the search to see all notifications"
                      : "Create your first notification to get started"}
                  </ArgonTypography>
                  {searchQuery.trim() ? (
                    <ArgonButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => setSearchQuery("")}
                    >
                      Clear Search
                    </ArgonButton>
                  ) : (
                    <ArgonButton
                      component={Link}
                      to="/notification/add"
                      variant="contained"
                      color="primary"
                      size="small"
                    >
                      Add Notification
                    </ArgonButton>
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>

        {/* dialog popup */}
        <Dialog
          open={deleteDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              overflow: "visible",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, position: "relative" }}>
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #ff6b6b, #ee5a52)",
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                }}
              >
                <Warning sx={{ color: "white", fontSize: "24px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" color="text" fontWeight="medium">
                  Delete Tutor
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  This action requires confirmation
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#6c757d",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                  color: "#dc3545",
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3 }}>
            <ArgonBox
              sx={{
                backgroundColor: "#fee",
                border: "1px solid #ffc1c1",
                borderRadius: "8px",
                p: 2.5,
                mb: 2,
              }}
            >
              <ArgonTypography variant="body1" color="#721c24" fontWeight="medium">
                Are you sure you want to delete this notification?
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" mt={1}>
                <strong>Title:</strong> {selectedNotification?.title || "N/A"}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                This action cannot be undone and will permanently remove this notification.
              </ArgonTypography>
            </ArgonBox>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
            <ArgonButton
              onClick={handleCloseDialog}
              variant="outlined"
              color="primary"
              disabled={loading}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              onClick={handleDeleteConfirm}
              variant="contained"
              startIcon={<Delete />}
              color="error"
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete Notification"}
            </ArgonButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

